import React, { useRef, useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { toPng } from 'html-to-image';
import jsPDF from 'jspdf';
import { TransactionItem } from '../../models/TransactionItem';
import { Button } from '../ui/Button';
import { exportToExcel, ExcelColumnConfig } from '../../utils/excelExport';
import TransactionPrintable, {
  formatTransactionDate,
  formatTransactionCurrency,
} from './TransactionPrintable';
import arrowLeft from '../../assets/images/arrow_left_grey.png';
import editIcon from '../../assets/images/edit.png';
import deleteIcon from '../../assets/images/delete.png';
import downloadIcon from '../../assets/images/download.png';
import shareIcon from '../../assets/images/share.png';

interface TransactionDetailProps {
  transaction: TransactionItem;
  onClose: () => void;
  onEdit?: (transaction: TransactionItem) => void;
  onDelete?: (transaction: TransactionItem) => void;
}

const TransactionDetail: React.FC<TransactionDetailProps> = ({ 
  transaction, 
  onClose, 
  onEdit, 
  onDelete 
}) => {
  const printableRef = useRef<HTMLDivElement>(null);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDownloadMenu(false);
      }
    };
    if (showDownloadMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDownloadMenu]);

    const formatDate = (dateString?: string) => formatTransactionDate(dateString);
  const formatCurrency = (amount?: number, currency?: string) => formatTransactionCurrency(amount, currency);
  const formatQuantity = (quantity?: number) => {
    if (quantity === undefined) return 'N/A';
    return quantity.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  };

  const handleEdit = () => onEdit?.(transaction);
  const handleDelete = () => onDelete?.(transaction);

  const captureAsImage = async (): Promise<string | null> => {
    if (!printableRef.current) return null;
    const wrapper = printableRef.current.parentElement;
    if (wrapper) {
      wrapper.style.overflow = 'visible';
      wrapper.style.height = 'auto';
      wrapper.style.position = 'fixed';
      wrapper.style.left = '0';
      wrapper.style.top = '0';
      wrapper.style.zIndex = '-9999';
      wrapper.style.opacity = '1';
    }
    try {
      const result = await toPng(printableRef.current, {
        backgroundColor: '#ffffff',
        pixelRatio: 2,
      });
      return result;
    } catch {
      return null;
    } finally {
      if (wrapper) {
        wrapper.style.overflow = 'hidden';
        wrapper.style.height = '0';
        wrapper.style.position = '';
        wrapper.style.left = '';
        wrapper.style.top = '';
        wrapper.style.zIndex = '';
        wrapper.style.opacity = '';
      }
    }
  };

  const handleDownloadImage = async () => {
    setShowDownloadMenu(false);
    const dataUrl = await captureAsImage();
    if (!dataUrl) {
      toast.error('Failed to generate image');
      return;
    }
    const link = document.createElement('a');
    link.download = `transaction_${transaction.getHashedId()}.png`;
    link.href = dataUrl;
    link.click();
    toast.success('Image downloaded');
  };

  const handleDownloadPDF = async () => {
    setShowDownloadMenu(false);
    const dataUrl = await captureAsImage();
    if (!dataUrl) {
      toast.error('Failed to generate PDF');
      return;
    }

    const img = new Image();
    img.src = dataUrl;
    await new Promise<void>((resolve) => { img.onload = () => resolve(); });

    const pixelRatio = 2;
    const pxToMm = 0.264583;
    const imgWidthMm = (img.width / pixelRatio) * pxToMm;
    const imgHeightMm = (img.height / pixelRatio) * pxToMm;

    const padding = 10;
    const pageWidth = imgWidthMm + padding * 2;
    const pageHeight = imgHeightMm + padding * 2;

    const pdf = new jsPDF({
      orientation: pageWidth > pageHeight ? 'landscape' : 'portrait',
      unit: 'mm',
      format: [pageWidth, pageHeight],
    });

    pdf.addImage(dataUrl, 'PNG', padding, padding, imgWidthMm, imgHeightMm);
    pdf.save(`transaction_${transaction.getHashedId()}.pdf`);
    toast.success('PDF downloaded');
  };

  const handleDownloadExcel = () => {
    setShowDownloadMenu(false);
    const columns: ExcelColumnConfig<TransactionItem>[] = [
      { key: 'id', header: 'Transaction ID', transform: (_, row) => row.getHashedId() },
      {
        key: 'dateTime',
        header: 'Date',
        transform: (v) => {
          if (!v) return '';
          const d = new Date(v);
          return isNaN(d.getTime()) ? v : d.toLocaleDateString();
        },
      },
      {
        key: 'dateTime',
        header: 'Time',
        transform: (v) => {
          if (!v) return '';
          const d = new Date(v);
          return isNaN(d.getTime()) ? '' : d.toLocaleTimeString();
        },
      },
      { key: 'action', header: 'Action', transform: (v) => v?.transaction_entity_name || '' },
      { key: 'direct_object', header: 'Object', transform: (v) => v?.transaction_entity_name || '' },
      { key: 'indirect_object', header: 'Vendor', transform: (v) => v?.transaction_entity_name || '' },
      { key: 'qty', header: 'Quantity', transform: (v) => formatQuantity(v) },
      { key: 'paymentMedia', header: 'Payment Media', transform: (v) => v?.transaction_entity_name || '' },
            { key: 'amount', header: 'Amount', transform: (v) => v ?? '' },
      { key: 'currency', header: 'Currency', transform: (v) => v || '' },
    ];

    exportToExcel({
      data: [transaction],
      columns,
      fileName: `transaction_${transaction.getHashedId()}`,
      sheetName: 'Transaction',
    });
    toast.success('Excel downloaded');
  };

  const handleShare = async () => {
    const fd = formatDate(transaction.dateTime);
    const text = [
      `Transaction: ${transaction.getHashedId()}`,
      `Date: ${fd.date}`,
      `Time: ${fd.time}`,
      `Action: ${transaction.action?.transaction_entity_name || 'N/A'}`,
      `Item: ${transaction.direct_object?.transaction_entity_name || 'N/A'}`,
      `Quantity: ${formatQuantity(transaction.qty)}`,
      `Payment: ${transaction.paymentMedia?.transaction_entity_name || 'N/A'}`,
      `Amount: ${formatCurrency(transaction.amount, transaction.currency)}`,
    ].join('\n');

    if (navigator.share) {
      try {
        await navigator.share({ title: `Transaction ${transaction.getHashedId()}`, text });
        return;
      } catch {
        // User cancelled or share failed — fall through to clipboard
      }
    }

    try {
      await navigator.clipboard.writeText(text);
      toast.success('Transaction details copied to clipboard');
    } catch {
      toast.error('Failed to copy to clipboard');
    }
  };

  const formattedDate = formatDate(transaction.dateTime);

  return (
    <div className="bg-light rounded-md p-4 sm:p-6 transition-all duration-300 ease-out">
      {/* Hidden printable component for image/PDF capture */}
      <div style={{ overflow: 'hidden', height: 0 }}>
        <TransactionPrintable ref={printableRef} transaction={transaction} />
      </div>

      {/* Header Section */}
      <div className="flex flex-row justify-between items-center mb-4">
        <div>
          <h2 className="text-xl text-dark">Transaction</h2>
          <div className="text-xs text-grey_dark mt-1">ID {transaction.getHashedId()}</div>
        </div>
        
        {/* Action Icons */}
        <div className="flex flex-row gap-2">
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setShowDownloadMenu(!showDownloadMenu)}
              className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-grey_light/80 transition-colors"
              aria-label="Download"
            >
              <img src={downloadIcon} alt="Download" className="object-contain w-8 h-8" />
            </button>

            {showDownloadMenu && (
              <div className="absolute right-0 top-10 bg-light border border-grey_light rounded-lg shadow-lg z-50 min-w-[160px]">
                <button
                  onClick={handleDownloadImage}
                  className="w-full text-left px-4 py-2.5 text-sm text-dark hover:bg-bg_grey_light transition-colors rounded-t-lg flex items-center gap-2"
                >
                  <svg className="w-4 h-4 text-grey_dark" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                  Image (PNG)
                </button>
                <button
                  onClick={handleDownloadPDF}
                  className="w-full text-left px-4 py-2.5 text-sm text-dark hover:bg-bg_grey_light transition-colors flex items-center gap-2"
                >
                  <svg className="w-4 h-4 text-grey_dark" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
                  PDF
                </button>
                <button
                  onClick={handleDownloadExcel}
                  className="w-full text-left px-4 py-2.5 text-sm text-dark hover:bg-bg_grey_light transition-colors rounded-b-lg flex items-center gap-2"
                >
                  <svg className="w-4 h-4 text-grey_dark" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                  Excel
                </button>
              </div>
            )}
          </div>
          <button
            onClick={handleEdit}
            className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-grey_light transition-colors"
            aria-label="Edit"
          >
            <img src={editIcon} alt="Edit" className="object-contain w-8 h-8" />
          </button>
          <button
            onClick={handleDelete}
            className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-grey_light/80 transition-colors"
            aria-label="Delete"
          >
            <img src={deleteIcon} alt="Delete" className="object-contain w-8 h-8" />
          </button>
          <button
            onClick={handleShare}
            className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-grey_light/80 transition-colors"
            aria-label="Share"
          >
            <img src={shareIcon} alt="Share" className="object-contain w-8 h-8" />
          </button>
        </div>
      </div>

      {/* Divider */}
      <div className="h-px bg-grey_light my-4" />

      {/* Transaction Details */}
      <div className="space-y-3">
        <div className="flex justify-between items-center py-2">
          <div className="text-sm text-grey_dark font-medium">Transaction ID</div>
          <div className="text-sm text-dark font-medium text-right">{transaction.getHashedId()}</div>
        </div>
        <div className="flex justify-between items-center py-2">
          <div className="text-sm text-grey_dark font-medium">Date</div>
          <div className="text-sm text-dark font-medium text-right">{formattedDate.date}</div>
        </div>
        <div className="flex justify-between items-center py-2">
          <div className="text-sm text-grey_dark font-medium">Time</div>
          <div className="text-sm text-dark font-medium text-right">{formattedDate.time}</div>
        </div>
        <div className="flex justify-between items-center py-2">
          <div className="text-sm text-grey_dark font-medium">Action/Transaction</div>
          <div className="text-sm text-dark font-medium text-right">{transaction.action?.transaction_entity_name || 'N/A'}</div>
        </div>
        <div className="flex justify-between items-center py-2">
          <div className="text-sm text-grey_dark font-medium">Item/Object</div>
          <div className="text-sm text-dark font-medium text-right">{transaction.direct_object?.transaction_entity_name || 'N/A'}</div>
        </div>
        <div className="flex justify-between items-center py-2">
          <div className="text-sm text-grey_dark font-medium">Vendor/Indirect Object</div>
          <div className="text-sm text-dark font-medium text-right">{transaction.indirect_object?.transaction_entity_name || 'N/A'}</div>
        </div>
        <div className="flex justify-between items-center py-2">
          <div className="text-sm text-grey_dark font-medium">Quantity</div>
          <div className="text-sm text-dark font-medium text-right">{formatQuantity(transaction.qty)}</div>
        </div>
        <div className="flex justify-between items-center py-2">
          <div className="text-sm text-grey_dark font-medium">Payment Medium</div>
          <div className="text-sm text-dark font-medium text-right">{transaction.paymentMedia?.transaction_entity_name || 'N/A'}</div>
        </div>
        <div className="flex justify-between items-center py-2">
          <div className="text-sm text-grey_dark font-medium">Total Amount</div>
          <div className="text-sm text-dark font-semibold text-right">{formatCurrency(transaction.amount, transaction.currency)}</div>
        </div>
      </div>

      {/* Back to List */}
      <div className="pt-6 mt-6 border-t border-grey_light flex gap-2">
        <Button
          type="button"
          onClick={onClose}
          variant="google"
          className="flex-1 flex items-center justify-center"
        >
          <img src={arrowLeft} alt="Back" className="object-contain w-4 h-4 mr-2" />
          Back to List
        </Button>
      </div>

      {/* Additional Details */}
      {(transaction.action?.transaction_entity_description || 
        transaction.direct_object?.transaction_entity_description || 
        transaction.indirect_object?.transaction_entity_description || 
        transaction.paymentMedia?.transaction_entity_description) && (
        <div className="mt-6 pt-4 border-t border-grey_light">
          <h4 className="text-sm font-medium text-grey_dark mb-3">Additional Details</h4>
          <div className="space-y-2">
            {transaction.action?.transaction_entity_description && (
              <div>
                <div className="text-xs text-grey_dark">Action Description</div>
                <div className="text-sm text-dark">{transaction.action.transaction_entity_description}</div>
              </div>
            )}
            {transaction.direct_object?.transaction_entity_description && (
              <div>
                <div className="text-xs text-grey_dark">Item Description</div>
                <div className="text-sm text-dark">{transaction.direct_object.transaction_entity_description}</div>
              </div>
            )}
            {transaction.indirect_object?.transaction_entity_description && (
              <div>
                <div className="text-xs text-grey_dark">Vendor Description</div>
                <div className="text-sm text-dark">{transaction.indirect_object.transaction_entity_description}</div>
              </div>
            )}
            {transaction.paymentMedia?.transaction_entity_description && (
              <div>
                <div className="text-xs text-grey_dark">Payment Description</div>
                <div className="text-sm text-dark">{transaction.paymentMedia.transaction_entity_description}</div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TransactionDetail;
